import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Chip, Box, Alert, CircularProgress, Paper
} from '@mui/material';
import { Restore } from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface DeletedDraft {
  _id: string;
  type: 'navigator' | 'banbam';
  deletedAt: string;
  lastSaved: string;
  deadline: string;
  userId: {
    _id: string;
    name: string;
    email: string;
  } | null;
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

const DeletedDraftsPage: React.FC = () => {
  const [drafts, setDrafts] = useState<DeletedDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeleted = async () => {
      try {
        const response = await axiosInstance.get<DeletedDraft[]>('/api/applications/drafts/deleted');
        setDrafts(response.data);
      } catch (err) {
        setError('Failed to load deleted drafts');
      } finally {
        setLoading(false);
      }
    };
    fetchDeleted();
  }, []);

  const handleRestore = async (draft: DeletedDraft) => {
    setRestoringId(draft._id);
    try {
      await axiosInstance.patch(`/api/applications/drafts/${draft._id}/restore`);
      setDrafts(prev => prev.filter(d => d._id !== draft._id));
      setSuccessMessage(`Restored ${draft.type} draft for ${draft.userId?.name ?? 'unknown user'}`);
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setError('Failed to restore draft');
    } finally {
      setRestoringId(null);
    }
  };

  const daysRemaining = (deletedAt: string) => {
    const expiry = new Date(deletedAt).getTime() + SEVEN_DAYS_MS;
    return Math.max(0, Math.ceil((expiry - Date.now()) / (1000 * 60 * 60 * 24)));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" gutterBottom>
        Deleted Application Drafts
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Artist-deleted drafts are kept for 7 days. Restore returns them to the artist's dashboard.
      </Typography>

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : drafts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No deleted drafts in the last 7 days.</Typography>
        </Paper>
      ) : (
        <Table component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell>Artist</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Last Saved</TableCell>
              <TableCell>Deleted</TableCell>
              <TableCell>Expires In</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map(draft => {
              const days = daysRemaining(draft.deletedAt);
              return (
                <TableRow key={draft._id}>
                  <TableCell>{draft.userId?.name ?? '—'}</TableCell>
                  <TableCell>{draft.userId?.email ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={draft.type}
                      size="small"
                      color={draft.type === 'navigator' ? 'primary' : 'secondary'}
                    />
                  </TableCell>
                  <TableCell>{new Date(draft.lastSaved).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(draft.deletedAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={days === 1 ? '1 day' : `${days} days`}
                      size="small"
                      color={days <= 2 ? 'error' : days <= 4 ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Restore />}
                      onClick={() => handleRestore(draft)}
                      disabled={restoringId === draft._id}
                    >
                      {restoringId === draft._id ? 'Restoring...' : 'Restore'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Container>
  );
};

export default DeletedDraftsPage;
